import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ShoppingBag, Clock, Package, Check, X,
  Phone, User, Calendar, Filter, RefreshCw, Bell, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-700', icon: Check },
  preparing: { label: 'En préparation', color: 'bg-purple-100 text-purple-700', icon: GustoIcon },
  ready: { label: 'Prête', color: 'bg-green-100 text-green-700', icon: Package },
  completed: { label: 'Récupérée', color: 'bg-gray-100 text-gray-700', icon: Check },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: X }
};

const NEXT_STATUS = {
  pending: 'confirmed',
  confirmed: 'preparing',
  preparing: 'ready',
  ready: 'completed'
};

export default function BusinessOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterDate]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch orders
      let url = `${API_URL}/api/business/orders?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterDate) url += `date=${filterDate}`;
      
      const ordersRes = await fetch(url, { headers });
      if (ordersRes.ok) {
        setOrders(await ordersRes.json());
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/business/orders/stats`, { headers });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Commande ${STATUS_CONFIG[newStatus].label.toLowerCase()}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="font-semibold text-charcoal flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Commandes
            </h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/business/pickup-slots">
                  <Clock className="w-4 h-4 mr-2" />
                  Gérer les créneaux
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-muted-foreground mb-1">Aujourd'hui</p>
              <p className="text-2xl font-bold text-charcoal">{stats.today.orders_count}</p>
              <p className="text-xs text-muted-foreground">commandes</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 shadow-sm border border-amber-200">
              <p className="text-sm text-amber-700 mb-1">En attente</p>
              <p className="text-2xl font-bold text-amber-700">{stats.today.pending}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 shadow-sm border border-purple-200">
              <p className="text-sm text-purple-700 mb-1">En préparation</p>
              <p className="text-2xl font-bold text-purple-700">{stats.today.preparing}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
              <p className="text-sm text-green-700 mb-1">Prêtes</p>
              <p className="text-2xl font-bold text-green-700">{stats.today.ready}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-muted-foreground mb-1">CA aujourd'hui</p>
              <p className="text-2xl font-bold text-charcoal">{stats.today.revenue.toFixed(2)}€</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setFilterStatus(''); setFilterDate(new Date().toISOString().split('T')[0]); }}
          >
            Réinitialiser
          </Button>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-charcoal mb-2">Aucune commande</h3>
            <p className="text-muted-foreground">
              {filterStatus || filterDate !== new Date().toISOString().split('T')[0]
                ? 'Aucune commande ne correspond à vos filtres.'
                : 'Les commandes de vos clients apparaîtront ici.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const canAdvance = NEXT_STATUS[order.status];
              
              return (
                <div key={order.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                        <StatusIcon className="w-4 h-4" />
                        {statusConfig.label}
                      </span>
                      <span className="font-mono font-bold text-charcoal">#{order.order_number}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      Retrait: {order.pickup_date} • {order.pickup_time}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      {/* Customer Info */}
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Client</p>
                        <p className="font-medium text-charcoal flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {order.customer_name}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {order.customer_phone}
                        </p>
                      </div>

                      {/* Order Items */}
                      <div className="md:col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">Articles ({order.items.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {order.items.map((item, idx) => (
                            <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {item.quantity}x {item.product_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {order.notes && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-amber-800">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          Note: {order.notes}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <span className="text-sm text-muted-foreground">Total: </span>
                        <span className="text-xl font-bold text-charcoal">{order.total.toFixed(2)}€</span>
                      </div>

                      <div className="flex items-center gap-2">
                        {order.status === 'pending' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-500"
                            onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                        )}
                        {canAdvance && (
                          <Button 
                            size="sm"
                            onClick={() => updateOrderStatus(order.id, canAdvance)}
                          >
                            {canAdvance === 'confirmed' && 'Confirmer'}
                            {canAdvance === 'preparing' && 'Commencer la préparation'}
                            {canAdvance === 'ready' && 'Marquer comme prête'}
                            {canAdvance === 'completed' && 'Marquer comme récupérée'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
